import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asRows } from '@/lib/api/list'
import type { BelgeInfoForm, BelgeSablon, PublishMode, SablonSurum, SurumStatus, TemplateMethod, VariableDef } from './types'
import { clearWizardDraft } from './lib/wizardDraft'
import { cloneSurumAsDraft, emptySistemSurum, stripPreview, validateForPublish } from './lib/templateHelpers'

export const docKeys = {
  all: ['hr', 'documents'] as const,
  templates: ['hr', 'documents', 'templates'] as const,
  template: (id: string) => ['hr', 'documents', 'templates', id] as const,
  variables: ['hr', 'documents', 'variables'] as const,
  types: ['hr', 'documents', 'types'] as const,
}

type Bundle = { sablon: BelgeSablon; surumler: SablonSurum[] }

function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function str(v: unknown, fallback = ''): string {
  if (typeof v === 'string') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return fallback
}

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v))) return Number(v)
  return fallback
}

const rows = (v: unknown): unknown[] => asRows(v)

function statusOf(v: unknown): SurumStatus {
  const status = str(v).toLowerCase()
  if (status.includes('publish') || status.includes('yayin') || status === 'active') return 'yayinda'
  if (status.includes('arch') || status.includes('arsiv')) return 'arsiv'
  return 'taslak'
}

function methodOf(v: unknown): TemplateMethod {
  const method = str(v).toLowerCase()
  if (method.includes('pdf')) return 'pdf'
  if (method.includes('docx') || method.includes('word') || method.includes('upload')) return 'docx'
  return 'sistem'
}

function sourceType(method: TemplateMethod) {
  if (method === 'pdf') return 'pdf'
  if (method === 'docx') return 'docx'
  return 'native_editor'
}

export function mapVariable(raw: unknown): VariableDef {
  const row = rec(raw)
  return {
    key: str(row.key ?? row.code),
    label: str(row.label ?? row.name ?? row.description, str(row.key)),
    group: str(row.group ?? row.module, 'diger'),
    sample: str(row.sample ?? row.example) || undefined,
  }
}

function surumFromVersion(templateId: string, raw: unknown, fallbackVersion: number): SablonSurum {
  const row = rec(raw)
  const content = rec(row.content_json ?? row.content)
  const editor = rec(content.editor)
  const version = num(row.version_number ?? row.version, fallbackVersion)
  const base = emptySistemSurum(templateId, version)
  const restored = editor.blocksBySection || editor.pages ? { ...base, ...(editor as Partial<SablonSurum>) } : base
  return {
    ...restored,
    id: str(row.id, restored.id),
    sablonId: templateId,
    version,
    status: statusOf(row.status ?? restored.status),
    changeReason: str(row.change_note ?? row.changeReason ?? restored.changeReason),
    method: methodOf(row.source_type ?? restored.method),
    createdAt: str(row.created_at, restored.createdAt),
    publishedAt: str(row.published_at) || restored.publishedAt,
  }
}

/**
 * Liste (`GET /templates`) ve detay (`GET /templates/{id}`) cevaplarını tek modele çevirir.
 * Şablon kaydının `status` alanı kayıt durumudur (active/archived); yayın durumu yayınlı sürümden okunur.
 */
export function mapSablon(raw: unknown): BelgeSablon {
  const row = rec(raw)
  const meta = rec(row.wizard_meta)
  const published = rec(row.published)
  const draft = rec(row.draft)
  const version = num(row.current_version ?? published.version_number, 0)
  const archived = str(row.status).toLowerCase() === 'archived'
  return {
    id: str(row.id),
    ad: str(row.name),
    kategori: str(row.type_code ?? row.document_type_id ?? row.type_id),
    aciklama: str(row.description),
    zorunlu: Boolean(row.is_required ?? meta.is_required),
    departmanIds: rows(meta.department_ids).map((item) => str(item)),
    pozisyonlar: rows(meta.position_ids).map((item) => str(item)),
    kontrolSorumlusu: str(meta.default_reviewer_employee_id),
    method: methodOf(row.source_type ?? draft.source_type ?? published.source_type),
    status: archived ? 'arsiv' : version > 0 ? 'yayinda' : 'taslak',
    currentVersion: version || 1,
    createdAt: str(row.created_at),
    updatedAt: str(row.updated_at ?? row.created_at),
    createdBy: str(row.created_by),
  }
}

function bundleFrom(raw: unknown): Bundle {
  const row = rec(raw)
  const template = rec(row.template).id ? rec(row.template) : row
  const sablon = mapSablon(template)
  const versions = [...(row.published ? [row.published] : []), ...(row.draft ? [row.draft] : [])]
  const surumler = versions.length
    ? versions.map((item, index) => surumFromVersion(sablon.id, item, index + 1))
    : [emptySistemSurum(sablon.id, sablon.currentVersion || 1)]
  const seen = new Set<string>()
  return {
    sablon,
    surumler: surumler.filter((item) => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    }),
  }
}

function draftBody(surum: SablonSurum) {
  const clean = stripPreview(surum)
  return {
    source_type: sourceType(surum.method),
    content_json: { editor: clean },
    field_schema_json: {
      variables: surum.fields.map((field) => ({
        key: field.variable || field.key,
        label: field.label,
        group: field.source,
        required: field.required,
      })),
      mappings: (surum.variableMappings ?? []).map((mapping) => ({
        template_var: mapping.templateVar,
        source: mapping.mappedFieldKey,
        status: mapping.status,
      })),
    },
    change_note: surum.changeReason,
  }
}

/** Kategori seçici tür id'si ya da kodu tutar; türler önbellekten çözülür (ek istek yok). */
async function resolveTypeId(kategori: string, types: { id: string; code: string; name: string }[]) {
  // Eski yerel taslaklarda kalan FE kategori kodu.
  const code = kategori === 'ise_giris' ? 'onboarding' : kategori
  const hit = types.find((item) => item.id === code || item.code === code || item.name === code)
  return hit ? hit.id : kategori
}

export async function listTemplates() {
  const data = await api.get<unknown>('/api/v1/documents/templates', { domain: 'employee' })
  return rows(data).map(mapSablon)
}

export async function loadTemplate(id: string): Promise<Bundle> {
  return bundleFrom(await api.get<unknown>(`/api/v1/documents/templates/${id}`))
}

/** Tür kodu → görünen ad (liste/başlıklar için). */
export function useCategoryLabel() {
  const types = useDocumentTypes().data
  return (code: string) => types?.find((x) => x.code === code || x.id === code)?.name ?? code
}

export function useTemplates() {
  return useQuery({ queryKey: docKeys.templates, queryFn: listTemplates })
}

export function useTemplateBundle(id: string | undefined) {
  return useQuery({ queryKey: docKeys.template(id ?? ''), queryFn: () => loadTemplate(id!), enabled: !!id })
}

export function useDocumentVariables() {
  return useQuery({
    queryKey: docKeys.variables,
    queryFn: async () => rows(await api.get<unknown>('/api/v1/documents/variables', { domain: 'employee' })).map(mapVariable),
  })
}

/** Belge kategorileri BE'deki personel belge türleridir (kod → ad). */
export function useDocumentTypes() {
  return useQuery({
    queryKey: docKeys.types,
    staleTime: 10 * 60_000,
    queryFn: async () =>
      rows(await api.get<unknown>('/api/v1/documents/types', { domain: 'employee' })).map((item) => {
        const row = rec(item)
        return { id: str(row.id), code: str(row.code ?? row.name), name: str(row.name ?? row.code) }
      }),
  })
}

export async function listDocumentCases(employeeId?: string) {
  return rows(await api.get<unknown>('/api/v1/documents/cases', { domain: 'employee', employee_id: employeeId }))
}

export async function listMissingDocuments(employeeId?: string) {
  return rows(await api.get<unknown>('/api/v1/documents/missing', { domain: 'employee', employee_id: employeeId }))
}

/** Taslağı tek istekte yazar (`/draft/save` aynı işlemin takma adıdır; ikinci çağrı gereksizdi). */
async function writeDraft(id: string, surum: SablonSurum) {
  await api.patch(`/api/v1/documents/templates/${id}/draft`, draftBody(surum))
}

export function uploadTemplateDraft(id: string, file: File) {
  const body = new FormData()
  body.append('file', file)
  return api.post(`/api/v1/documents/templates/${id}/draft/upload`, body)
}

export async function previewTemplate(id: string, input: { mode: 'empty' | 'filled'; employeeId?: string }) {
  return api.post<unknown>(`/api/v1/documents/templates/${id}/preview`, {
    mode: input.mode,
    employee_id: input.mode === 'filled' ? input.employeeId : undefined,
  })
}

function publishBody(mode: PublishMode, changeNote: string, employeeIds: string[]) {
  if (mode === 'selected_employees') {
    return { change_note: changeNote, create_requests_for: 'selected', employee_ids: employeeIds }
  }
  // Kapsama (departman/pozisyon) uyan tüm mevcut personele talep açılır; yeni personel zaten otomatik alır.
  if (mode === 'all_matching') return { change_note: changeNote, create_requests_for: 'all_matching' }
  return { change_note: changeNote, create_requests_for: 'none' }
}

export async function createTemplateFromWizard(info: BelgeInfoForm, surum: SablonSurum, types: { id: string; code: string; name: string }[]) {
  const typeId = await resolveTypeId(info.kategori, types)
  const created = rec(
    await api.post('/api/v1/documents/templates', {
      name: info.ad.trim(),
      type_id: typeId,
      description: info.aciklama.trim(),
      department_ids: info.departmanIds,
      position_ids: info.pozisyonlar,
      is_required: info.zorunlu,
      default_reviewer_employee_id: info.kontrolSorumlusu.trim() || undefined,
    }),
  )
  const id = str(created.id ?? rec(created.template).id)
  const saved = { ...surum, id: str(rec(created.draft).id, surum.id), sablonId: id, status: 'taslak' as const, version: 1 }
  await writeDraft(id, saved)
  clearWizardDraft()
  return { sablon: mapSablon({ ...created, id, name: info.ad, type_id: typeId, description: info.aciklama }), surum: saved }
}

export async function saveTemplateDraft(surum: SablonSurum) {
  await writeDraft(surum.sablonId, surum)
  return surum
}

export async function publishTemplate(surum: SablonSurum, opts: { changeReason: string; mode: PublishMode; employeeIds: string[] }) {
  const errors = validateForPublish(surum)
  if (errors.length) return { ok: false as const, errors }
  const next = { ...surum, changeReason: opts.changeReason }
  await writeDraft(surum.sablonId, next)
  await api.post(`/api/v1/documents/templates/${surum.sablonId}/publish`, publishBody(opts.mode, opts.changeReason, opts.employeeIds))
  return { ok: true as const, surum: { ...next, status: 'yayinda' as const } }
}

export function ensureLocalDraft(surumler: SablonSurum[], sablon: BelgeSablon): SablonSurum {
  const draft = surumler.find((item) => item.status === 'taslak')
  if (draft) return structuredClone(draft)
  const published = surumler.find((item) => item.status === 'yayinda')
  if (published) return cloneSurumAsDraft(published, published.version + 1)
  return emptySistemSurum(sablon.id, (sablon.currentVersion || 0) + 1)
}

export function useSaveTemplateDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: saveTemplateDraft,
    onSuccess: (_data, surum) => {
      void qc.invalidateQueries({ queryKey: docKeys.template(surum.sablonId) })
      void qc.invalidateQueries({ queryKey: docKeys.templates })
    },
  })
}

/** Belge talebine dosya gönderir (multipart; dosya sunucuda doğrulanıp şifreli saklanır). */
export function useSubmitDocumentCase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { caseId: string; employeeId?: string; file: File; note?: string }) => {
      const form = new FormData()
      form.append('file', input.file)
      if (input.note) form.append('note', input.note)
      return api.post(`/api/v1/documents/cases/${input.caseId}/submit`, form)
    },
    onSuccess: (_d, { employeeId }) => {
      if (employeeId) {
        void qc.invalidateQueries({ queryKey: ['hr', 'employees', employeeId, 'cases'] })
        void qc.invalidateQueries({ queryKey: ['hr', 'employees', employeeId, 'missing'] })
      }
    },
  })
}
